<script setup lang="ts">
import type { BrokerAboutEmits, BrokerAboutProps } from '.';

import { cn } from '@/utils';

import { OverflowContainer } from '~/components/OverflowContainer';
import { Skeleton } from '~/components/ui/skeleton';

const props = withDefaults(defineProps<BrokerAboutProps>(), {
	isLoading: false,
	bio: '',
	title: 'Sobre',
	numberOfSkeletonLines: 3
});

const emit = defineEmits<BrokerAboutEmits>();

const description = computed<string>(() => props.bio?.trim() ?? '');

const hasDescription = computed<boolean>(() => description.value.length > 0);

const shouldRender = computed<boolean>(
	() => props.isLoading || hasDescription.value
);

function handleExpandedChange(isExpanded: boolean): void {
	if (isExpanded) emit('see-more-click');
}
</script>

<template>
	<section
		v-if="shouldRender"
		:class="
			cn(
				'flex w-full flex-col gap-6 border-b border-white-dark-3 pb-8 lg:pb-12',
				props.class
			)
		"
		:aria-busy="isLoading"
	>
		<div
			v-if="isLoading"
			class="flex h-9 items-center lg:h-12"
			aria-hidden="true"
		>
			<Skeleton
				is-active
				variant="white"
				class="h-[29px] w-[210px] max-w-full rounded-none motion-reduce:animate-none lg:translate-y-0.5"
			/>
		</div>
		<h2
			v-else
			class="font-matter-sq text-2xl font-normal leading-[1.5] tracking-[-0.02em] text-primary lg:text-[32px]"
		>
			{{ title }}
		</h2>

		<div v-if="isLoading" class="flex w-full flex-col gap-3" aria-hidden="true">
			<div class="flex w-full flex-col">
				<div
					v-for="line in numberOfSkeletonLines"
					:key="`broker-about-skeleton-${line}`"
					class="h-7 w-full pt-2"
				>
					<Skeleton
						is-active
						variant="white"
						class="h-4 w-full rounded-none motion-reduce:animate-none"
					/>
				</div>
			</div>
			<div class="flex h-[26px] items-center gap-2">
				<div class="flex size-6 items-center pl-1.5">
					<Skeleton
						is-active
						variant="white"
						class="size-4 rounded-none motion-reduce:animate-none"
					/>
				</div>
				<Skeleton
					is-active
					variant="white"
					class="h-4 w-[65px] rounded-none motion-reduce:animate-none"
				/>
			</div>
		</div>

		<OverflowContainer
			v-else
			max-height="max-h-[84px]"
			container-class="min-h-[122px] gap-3"
			button-class="h-[26px] items-center gap-2"
			@update:model-value="handleExpandedChange"
		>
			<p class="font-sans text-base font-normal leading-7 text-primary">
				{{ description }}
			</p>
		</OverflowContainer>
	</section>
</template>
